/**
 * The Location / Locus 360 — the first entity to use the generic infrastructure, and therefore the one
 * that proves it.
 *
 * It is a descriptor, not a screen. Everything below the header comes free: activity, audit, documents,
 * approvals, exceptions and integration provenance. What is written here is only what is *particular* to a
 * location — its identity, its place in the site hierarchy, and where it came from.
 *
 * The same shape will serve Person, Chiller, Barrel, Batch, Vehicle, Customer, Order and Delivery. If the
 * next one needs a change to this file rather than a new descriptor, the abstraction is wrong.
 */
import { Link } from 'react-router-dom';
import { Building2, MapPin, Warehouse } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import {
  Fact, FactGrid, Panel, PanelHeader, SectionLabel, Status, StatusBadge, Unknown, cx,
} from '../../ui/primitives';
import { ProvenanceSection } from '../../entity360/sections';
import type { Entity360Descriptor } from '../../entity360/types';
import { dateTime as fmtDateTime } from '../../ui/format';

/** The registry owns these paths; a Major and a Minor Locus each have their own 360 route. */
export function locusPath(kind: string, id: string): string {
  return kind === 'MINOR_LOCUS' ? `/operations/minor-loci/${id}` : `/operations/major-loci/${id}`;
}

export interface Location360 {
  entity_type: string;
  id: string;
  code: string;
  name: string;
  kind: 'MAJOR_LOCUS' | 'MINOR_LOCUS' | 'STANDALONE' | 'PLANT' | 'HUB' | 'COLD_ROOM' | 'OFFICE' | 'WAREHOUSE' | 'OTHER';
  status: 'ACTIVE' | 'INACTIVE' | 'RETIRED';
  lat: number | null;
  lng: number | null;
  data_origin: string;
  source_record_id: string | null;
  created_at: string;
  updated_at: string;
  parent: { id: string; code: string; name: string; kind: string } | null;
  city: { id: string; name: string } | null;
  children: { id: string; code: string; name: string; kind: string; status: string }[];
  ancestors: { id: string; depth: number }[];
  counts: { children: number; documents: number; events: number; approvals: number; exceptions: number };
}

const KIND_LABEL: Record<Location360['kind'], string> = {
  MAJOR_LOCUS: 'Major Locus',
  MINOR_LOCUS: 'Minor Locus',
  STANDALONE: 'Standalone',
  PLANT: 'Plant',
  HUB: 'Hub',
  COLD_ROOM: 'Cold room',
  OFFICE: 'Office',
  WAREHOUSE: 'Warehouse',
  OTHER: 'Other',
};

function LocationOverview({ entity }: { entity: Location360 }) {
  return (
    <div>
      <FactGrid columns={4}>
        <Fact label="Code" mono>
          {entity.code}
        </Fact>
        <Fact label="Kind">{KIND_LABEL[entity.kind]}</Fact>
        <Fact label="Status">
          <Status code={entity.status === 'ACTIVE' ? 'HEALTHY' : 'DISABLED'} fallbackWord={entity.status} />
        </Fact>
        <Fact label="City">{entity.city?.name}</Fact>

        <Fact label="Parent">
          {entity.parent ? (
            <Link to={locusPath(entity.parent.kind, entity.parent.id)} className="text-brand hover:underline">
              {entity.parent.code}
            </Link>
          ) : (
            <Unknown title="This location is at the top of its tree" />
          )}
        </Fact>
        <Fact label="Latitude" mono>
          {entity.lat}
        </Fact>
        <Fact label="Longitude" mono>
          {entity.lng}
        </Fact>
        <Fact label="Origin">
          {entity.data_origin === 'SIMULATION' ? <Status code="SIMULATION" /> : entity.data_origin}
        </Fact>
      </FactGrid>

      <div className="border-t border-line px-4 py-3">
        <SectionLabel>Created</SectionLabel>
        <p className="mt-0.5 text-small text-ink-muted">
          {fmtDateTime(entity.created_at)} · last changed{' '}
          {fmtDateTime(entity.updated_at)}
        </p>
      </div>
    </div>
  );
}

function LocationRelationships({ entity }: { entity: Location360 }) {
  if (entity.children.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-body">Nothing hangs off this location</p>
        <p className="mt-1 text-small text-ink-muted">
          {entity.kind === 'MINOR_LOCUS'
            ? 'A Minor Locus is a leaf: it supplies its Major Locus and has no children of its own.'
            : 'Child locations will appear here as they are created or mirrored.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="border-b border-line px-4 py-2">
        <SectionLabel>
          {entity.children.length} location{entity.children.length === 1 ? '' : 's'} below this one
        </SectionLabel>
      </div>
      <ul className="divide-y divide-line">
        {entity.children.map((child) => (
          <li key={child.id}>
            <Link
              to={locusPath(child.kind, child.id)}
              className="flex h-[var(--height-row)] items-center gap-3 px-4 hover:bg-brand-soft"
            >
              <span className="code-text w-40 shrink-0 truncate text-ink-muted">{child.code}</span>
              <span className="min-w-0 flex-1 truncate text-body">{child.name}</span>
              <span className="label-text shrink-0">{KIND_LABEL[child.kind as Location360['kind']] ?? child.kind}</span>
              <span className={cx('shrink-0 text-small', child.status !== 'ACTIVE' && 'text-ink-faint')}>
                {child.status === 'ACTIVE' ? '' : child.status}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const locationDescriptor: Entity360Descriptor<Location360> = {
  entityType: 'core.locations',
  kind: 'locations',
  registerPath: '/operations/minor-loci',
  registerLabel: 'Minor Loci',

  load: (id) => rpc<Location360>('location_360', { p_id: id }),
  title: (e) => e.name,
  code: (e) => e.code,

  status: (e) => (
    <div className="flex items-center gap-2">
      {e.data_origin === 'SIMULATION' ? <Status code="SIMULATION" /> : null}
      <StatusBadge
        tone={e.status === 'ACTIVE' ? 'accepted' : 'neutral'}
        icon={e.kind === 'PLANT' ? Warehouse : e.kind === 'MINOR_LOCUS' ? MapPin : Building2}
      >
        {KIND_LABEL[e.kind]}
      </StatusBadge>
    </div>
  ),

  summary: (e) => (
    <dl className="flex flex-wrap gap-x-8 gap-y-1">
      <div>
        <dt className="label-text">Parent</dt>
        <dd className="code-text">{e.parent?.code ?? '—'}</dd>
      </div>
      <div>
        <dt className="label-text">Below this</dt>
        <dd className="tabular">{e.counts.children}</dd>
      </div>
      <div>
        <dt className="label-text">Events</dt>
        <dd className="tabular">{e.counts.events}</dd>
      </div>
      <div>
        <dt className="label-text">Open exceptions</dt>
        <dd className={cx('tabular', e.counts.exceptions > 0 && 'font-medium text-refused')}>
          {e.counts.exceptions}
        </dd>
      </div>
    </dl>
  ),

  rail: (entity, context) => (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title="Where it came from" />
        <ProvenanceSection {...context} />
      </Panel>
      {entity.parent ? (
        <Panel>
          <PanelHeader title="Site hierarchy" />
          <ul className="divide-y divide-line">
            <li className="px-4 py-2">
              <Link to={locusPath(entity.parent.kind, entity.parent.id)} className="block hover:text-brand">
                <span className="code-text text-ink-muted">{entity.parent.code}</span>
                <span className="block truncate text-small">{entity.parent.name}</span>
              </Link>
            </li>
            <li className="bg-brand-soft/40 px-4 py-2 pl-6">
              <span className="code-text text-brand">{entity.code}</span>
              <span className="block truncate text-small">{entity.name}</span>
            </li>
          </ul>
        </Panel>
      ) : null}
    </div>
  ),

  tabs: [
    { id: 'overview', label: 'Overview', section: 'overview', render: (e) => <LocationOverview entity={e} /> },
    {
      id: 'relationships',
      label: 'Below this',
      section: 'relationships',
      count: (e) => e.counts.children,
      render: (e) => <LocationRelationships entity={e} />,
    },
    { id: 'activity', label: 'Activity', section: 'activity', count: (e) => e.counts.events },
    { id: 'documents', label: 'Documents', section: 'documents', count: (e) => e.counts.documents },
    { id: 'approvals', label: 'Approvals', section: 'approvals', count: (e) => e.counts.approvals, permission: 'approvals.view' },
    { id: 'exceptions', label: 'Exceptions', section: 'exceptions', count: (e) => e.counts.exceptions, permission: 'integration.view' },
    { id: 'provenance', label: 'Source', section: 'provenance', permission: 'integration.view' },
    { id: 'audit', label: 'Audit', section: 'audit', permission: 'audit.view' },
  ],
};
